import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  testimonialInputSchema,
  testimonialSchema,
  type Testimonial,
  type TestimonialInput,
} from '../schemas/testimonial';

const COLLECTION = 'testimonials';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): Testimonial {
  const data = raw as Record<string, unknown>;
  return testimonialSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

export async function listPublishedTestimonials(): Promise<Testimonial[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('published', '==', true),
    orderBy('displayOrder', 'asc'),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listAllTestimonials(): Promise<Testimonial[]> {
  const q = query(collection(db(), COLLECTION), orderBy('displayOrder', 'asc'));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function getTestimonial(id: string): Promise<Testimonial | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function createTestimonial(input: TestimonialInput): Promise<string> {
  const parsed = testimonialInputSchema.parse(input);
  const now = new Date().toISOString();
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateTestimonial(
  id: string,
  patch: Partial<TestimonialInput>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTestimonial(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
